import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["main", "thumb", "modal", "modalImage", "counter", "caption", "modalThumb"]
  static values = {
    photos: Array,
    index: { type: Number, default: 0 }
  }

  connect() {
    this.boundKeydown = this.handleKeydown.bind(this)
  }

  disconnect() {
    this.close()
  }

  select(event) {
    const button = event.currentTarget

    if (event.type === "click") event.preventDefault()

    const index = Number(button.dataset.productGalleryIndex)
    if (!Number.isNaN(index)) this.indexValue = index

    this.mainTarget.src = button.dataset.src
    this.mainTarget.alt = button.dataset.alt || ""
    this.thumbTargets.forEach((thumb) => thumb.setAttribute("aria-pressed", "false"))
    button.setAttribute("aria-pressed", "true")
  }

  open(event) {
    event.preventDefault()
    event.stopPropagation()
    if (!this.hasModalTarget || !this.hasPhotos()) return

    this.previouslyFocused = document.activeElement
    if (!this.modalTarget.open) this.modalTarget.showModal()
    document.body.classList.add("overflow-hidden")
    document.addEventListener("keydown", this.boundKeydown)
    this.renderModal()
    this.modalTarget.querySelector(".product-gallery-modal__close")?.focus()
  }

  close(event) {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }

    if (this.hasModalTarget && this.modalTarget.open) this.modalTarget.close()
    if (this.hasModalImageTarget) this.modalImageTarget.removeAttribute("src")
    document.removeEventListener("keydown", this.boundKeydown)
    document.body.classList.remove("overflow-hidden")

    if (event && this.previouslyFocused instanceof HTMLElement) {
      this.previouslyFocused.focus()
    }
  }

  previous(event) {
    event?.preventDefault()
    event?.stopPropagation()
    if (!this.hasPhotos()) return

    this.indexValue = (this.indexValue - 1 + this.photosValue.length) % this.photosValue.length
    this.renderModal()
  }

  next(event) {
    event?.preventDefault()
    event?.stopPropagation()
    if (!this.hasPhotos()) return

    this.indexValue = (this.indexValue + 1) % this.photosValue.length
    this.renderModal()
  }

  show(event) {
    event.preventDefault()
    event.stopPropagation()

    const index = Number(event.currentTarget.dataset.productGalleryIndex)
    if (Number.isNaN(index)) return

    this.indexValue = index
    this.renderModal()
  }

  handleBackdrop(event) {
    if (event.target === this.modalTarget) this.close(event)
  }

  handleKeydown(event) {
    if (event.key === "Escape") {
      this.close(event)
    } else if (event.key === "ArrowLeft") {
      this.previous(event)
    } else if (event.key === "ArrowRight") {
      this.next(event)
    }
  }

  renderModal() {
    const photo = this.photosValue[this.indexValue]
    if (!photo) return

    this.modalImageTarget.src = photo.fullscreen_url || photo.large_url
    this.modalImageTarget.alt = photo.label || photo.filename || ""
    this.counterTarget.textContent = `${this.indexValue + 1} / ${this.photosValue.length}`
    this.captionTarget.textContent = photo.filename || photo.label || ""

    if (this.hasMainTarget) {
      this.mainTarget.src = photo.large_url
      this.mainTarget.alt = photo.label || photo.filename || ""
    }
    this.thumbTargets.forEach((thumb, index) => {
      thumb.setAttribute("aria-pressed", String(index === this.indexValue))
    })
    this.modalThumbTargets.forEach((thumb, index) => {
      thumb.setAttribute("aria-pressed", String(index === this.indexValue))
    })
  }

  hasPhotos() {
    return this.hasPhotosValue && this.photosValue.length > 0
  }
}
